<script setup lang="ts">
/**
 * Строка-событие вызова инструмента (макеты 8-2, 8-2a, 8-2b).
 *
 * Свёрнута по умолчанию: за один ответ модель ходит до шести раз, и развёрнутые
 * выдачи вытеснили бы с экрана сам ответ. Развернуть можно ради «что он вообще
 * спросил» — под шевроном лежат аргументы и ответ как есть.
 *
 * Отказ строку не разворачивает: почему не вышло, модель говорит следующей же
 * репликой (макет 8-2b), и второй текст ошибки рядом с ним — то же самое
 * дважды. Машинная причина остаётся в подсказке.
 */
import { ChevronRight } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PanelCall } from '@/stores/assistant'

import { formatDuration } from './tools'

const props = defineProps<{ item: PanelCall; canRetry: boolean }>()

const emit = defineEmits<{ retry: [] }>()

const { t, locale } = useI18n()

const isOpen = ref(false)

const isError = computed(() => props.item.state === 'error')

/** Подпись справа от имени: «1 задача · 0,4 с», «идёт…», «не удалось». */
const meta = computed(() => {
  if (props.item.state === 'running') return t('assistant.tool.running')
  if (isError.value) return t('assistant.tool.failed')

  const parts: string[] = []
  const summary = props.item.summary

  if (summary?.kind === 'count') parts.push(t(`assistant.tool.${summary.of}`, summary.value))
  if (summary?.kind === 'subject') parts.push(summary.value)

  if (props.item.durationMs) {
    parts.push(t('assistant.tool.seconds', { value: formatDuration(props.item.durationMs, locale.value) }))
  }

  return parts.join(' · ')
})
</script>

<template>
  <div class="trow">
    <div class="trow__line" :class="{ 'trow__line--error': isError }">
      <button
        type="button"
        class="trow__head"
        :disabled="isError"
        :aria-expanded="isOpen"
        :title="item.errorMessage || undefined"
        @click="isOpen = !isOpen"
      >
        <span class="trow__marker" :class="`trow__marker--${item.state}`" />
        <span class="trow__name">{{ item.name }}</span>
        <span class="trow__meta">{{ meta }}</span>

        <ChevronRight
          v-if="!isError"
          class="trow__chevron"
          :class="{ 'trow__chevron--open': isOpen }"
          :size="16"
        />
      </button>

      <button
        v-if="isError"
        type="button"
        class="trow__retry"
        :disabled="!canRetry"
        @click="emit('retry')"
      >
        {{ t('assistant.tool.retry') }}
      </button>
    </div>

    <pre v-if="isOpen && item.details" class="trow__details">{{ item.details }}</pre>
  </div>
</template>

<style scoped>
.trow {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.trow__line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  padding: 0 8px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.trow__head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 6px 0;
  border: none;
  background: none;
  font-family: var(--font-family-sans);
  text-align: left;
  cursor: pointer;
}

.trow__head:disabled {
  cursor: default;
}

.trow__marker {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--radius-full);
}

.trow__marker--running {
  background: var(--color-info);
}

.trow__marker--done {
  background: var(--color-success);
}

.trow__marker--error {
  background: var(--color-danger);
}

/* Имя инструмента — то, как его зовут в реестре: переводить нечего. */
.trow__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.trow__meta {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

/* Отказ читается громче удачи: имя в основном цвете, подпись — в опасном. */
.trow__line--error .trow__name {
  color: var(--color-text-primary);
}

.trow__line--error .trow__meta {
  color: var(--color-danger);
}

.trow__chevron {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  transition: transform 120ms ease;
}

.trow__chevron--open {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .trow__chevron {
    transition: none;
  }
}

.trow__retry {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.trow__retry:disabled {
  color: var(--color-text-muted);
  cursor: default;
}

.trow__details {
  max-height: 200px;
  margin: 0;
  padding: 8px;
  overflow: auto;
  border-radius: var(--radius-sm);
  background: var(--color-bg-app);
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  line-height: 16px;
}
</style>
